import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faStar, faArrowLeft, faLightbulb,
} from '@fortawesome/free-solid-svg-icons';
import { AREA_CONFIG } from './LevelUpHubPage';
import api from '../api/API';
import './SkillsPage.css';

const emptyForm = () => ({
  nome: '',
  area: 'carreira',
  nivel: 1,
  descricao: '',
  evidencias: '',
});

const SkillsPage = () => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterArea, setFilterArea] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setLoading(true);
        const res = await api.get('/pdi/skills');
        setSkills(res?.data?.records || []);
      } catch (err) {
        setError('Erro ao carregar habilidades.');
      } finally {
        setLoading(false);
      }
    };
    fetchSkills();
  }, []);

  const filteredSkills = useMemo(
    () => filterArea === 'all' ? skills : skills.filter(s => s.area === filterArea),
    [skills, filterArea]
  );

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
    setError('');
  };

  const handleOpenEdit = (skill) => {
    setEditingId(skill.id);
    setForm({
      nome: skill.nome,
      area: skill.area,
      nivel: skill.nivel || 1,
      descricao: skill.descricao || '',
      evidencias: Array.isArray(skill.evidencias) ? skill.evidencias.join('\n') : '',
    });
    setShowModal(true);
    setError('');
  };

  const handleSave = useCallback(async () => {
    if (!form.nome.trim()) return;
    const evidencias = form.evidencias
      .split('\n')
      .map(e => e.trim())
      .filter(Boolean);

    setSaving(true);
    setError('');
    try {
      if (editingId) {
        const res = await api.put(`/pdi/skills/${editingId}`, { ...form, evidencias });
        const updated = res?.data?.record;
        setSkills(prev => prev.map(s => s.id === editingId ? updated : s));
      } else {
        const res = await api.post('/pdi/skills', { ...form, evidencias });
        const created = res?.data?.record;
        setSkills(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao salvar habilidade.');
    } finally {
      setSaving(false);
    }
  }, [form, editingId]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Excluir esta habilidade?')) return;
    try {
      await api.delete(`/pdi/skills/${id}`);
      setSkills(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setError('Erro ao excluir habilidade.');
    }
  }, []);

  const renderStars = (nivel, color) => {
    const filled = Math.min(5, Math.ceil((nivel / 10) * 5));
    return Array.from({ length: 5 }, (_, i) => (
      <FontAwesomeIcon
        key={i}
        icon={faStar}
        className={`skill-star ${i < filled ? 'filled' : 'empty'}`}
        style={{ color: i < filled ? (color || '#f59e0b') : 'rgba(255,255,255,0.1)' }}
      />
    ));
  };

  return (
    <div className="skills-page">
      <div className="skills-header">
        <div className="skills-header-left">
          <button
            style={{ background: 'transparent', border: 'none', color: 'rgba(226,232,240,0.5)', cursor: 'pointer', marginBottom: 8, fontSize: '0.84rem' }}
            onClick={() => navigate('/levelup')}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
            LevelUp Hub
          </button>
          <h1>Habilidades</h1>
          <p>{skills.length} habilidade{skills.length !== 1 ? 's' : ''} registrada{skills.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="skills-add-btn" onClick={handleOpenAdd} disabled={saving}>
          <FontAwesomeIcon icon={faPlus} />
          Nova Habilidade
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', marginBottom: 16, fontSize: '0.88rem' }}>
          {error}
        </div>
      )}

      {/* Filter tabs */}
      <div className="skills-filter-tabs">
        <div
          className={`skills-filter-tab${filterArea === 'all' ? ' active' : ''}`}
          onClick={() => setFilterArea('all')}
        >
          Todas ({skills.length})
        </div>
        {AREA_CONFIG.map(cfg => {
          const count = skills.filter(s => s.area === cfg.id).length;
          if (count === 0) return null;
          return (
            <div
              key={cfg.id}
              className={`skills-filter-tab${filterArea === cfg.id ? ' active' : ''}`}
              style={filterArea === cfg.id ? { background: `${cfg.color}18`, borderColor: `${cfg.color}44`, color: cfg.color } : {}}
              onClick={() => setFilterArea(cfg.id)}
            >
              <FontAwesomeIcon icon={cfg.icon} style={{ marginRight: 5 }} />
              {cfg.label} ({count})
            </div>
          );
        })}
      </div>

      {loading ? (
        <p style={{ color: 'rgba(226,232,240,0.4)', textAlign: 'center', padding: '40px 0' }}>Carregando habilidades...</p>
      ) : (
        <div className="skills-grid">
          {filteredSkills.map(skill => {
            const cfg = AREA_CONFIG.find(a => a.id === skill.area) || AREA_CONFIG[0];
            const pct = Math.min(100, Math.round((skill.nivel / 10) * 100));
            const evidencias = Array.isArray(skill.evidencias) ? skill.evidencias : [];
            return (
              <div
                key={skill.id}
                className="skill-card"
                style={{ '--skill-color': cfg.color, '--skill-glow': cfg.glow }}
              >
                <div className="skill-card-header">
                  <div>
                    <p className="skill-card-name">{skill.nome}</p>
                    <span
                      className="skill-card-area-badge"
                      style={{ background: `${cfg.color}18`, border: `1px solid ${cfg.color}33`, color: cfg.color }}
                    >
                      <FontAwesomeIcon icon={cfg.icon} />
                      {cfg.label}
                    </span>
                  </div>
                  <div className="skill-card-actions">
                    <button className="skill-card-btn" onClick={() => handleOpenEdit(skill)} title="Editar">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="skill-card-btn danger" onClick={() => handleDelete(skill.id)} title="Excluir">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                <div className="skill-stars">{renderStars(skill.nivel, cfg.color)}</div>

                <div className="skill-level-wrap">
                  <div className="skill-level-row">
                    <span className="skill-level-label">Nível {skill.nivel}/10</span>
                    <span className="skill-level-num">{pct}%</span>
                  </div>
                  <div className="skill-level-bar">
                    <div className="skill-level-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                {skill.descricao && <p className="skill-desc">{skill.descricao}</p>}

                {evidencias.length > 0 && (
                  <div>
                    <p className="skill-evidence-label">Evidências</p>
                    <div className="skill-evidence-list">
                      {evidencias.slice(0, 3).map((ev, i) => (
                        <div key={i} className="skill-evidence-item">
                          <div className="skill-evidence-dot" />
                          {ev}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="skill-card-add" onClick={handleOpenAdd}>
            <FontAwesomeIcon icon={faLightbulb} className="skill-card-add-icon" />
            <p style={{ margin: 0, fontSize: '0.88rem' }}>Nova habilidade</p>
            <span>Registre o que você sabe fazer</span>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="skills-modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="skills-modal">
            <h2 className="skills-modal-title">
              {editingId ? 'Editar Habilidade' : 'Nova Habilidade'}
            </h2>

            <div className="skills-modal-field">
              <label className="skills-modal-label">Nome *</label>
              <input
                className="skills-modal-input"
                value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: React, Liderança, Inglês avançado..."
                autoFocus
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="skills-modal-field">
                <label className="skills-modal-label">Área</label>
                <select
                  className="skills-modal-select"
                  value={form.area}
                  onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
                >
                  {AREA_CONFIG.map(a => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              </div>

              <div className="skills-modal-field">
                <label className="skills-modal-label">Nível: {form.nivel}/10</label>
                <input
                  type="range"
                  className="skills-modal-range"
                  min="1"
                  max="10"
                  value={form.nivel}
                  onChange={e => setForm(f => ({ ...f, nivel: Number(e.target.value) }))}
                />
                <div className="skills-level-stars">
                  {Array.from({ length: 5 }, (_, i) => (
                    <FontAwesomeIcon
                      key={i}
                      icon={faStar}
                      style={{ fontSize: '1rem', color: i < Math.ceil((form.nivel / 10) * 5) ? '#f59e0b' : 'rgba(255,255,255,0.12)' }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="skills-modal-field">
              <label className="skills-modal-label">Descrição</label>
              <textarea
                className="skills-modal-textarea"
                value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                placeholder="Como você usa essa habilidade, contexto..."
              />
            </div>

            <div className="skills-modal-field">
              <label className="skills-modal-label">Evidências (uma por linha)</label>
              <textarea
                className="skills-modal-textarea"
                value={form.evidencias}
                onChange={e => setForm(f => ({ ...f, evidencias: e.target.value }))}
                placeholder={"Construí 5 apps em produção com React\nLiderei equipe de 4 desenvolvedores"}
              />
            </div>

            {error && (
              <p style={{ color: '#fca5a5', fontSize: '0.84rem', margin: '0 0 12px' }}>{error}</p>
            )}

            <div className="skills-modal-footer">
              <button className="skills-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="skills-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsPage;
